import {
  Chip,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import styles from './LockedExpenseMessage.module.scss';
import { LockedReasonEnum } from './types';
import { Icon } from '@/components/ui/Icon';
import { areaIcons } from '@/constants/areaIcons';

interface LockedExpenseMessageProps {
  reason: LockedReasonEnum;
}

export const LockedExpenseMessage = ({ reason }: LockedExpenseMessageProps) => {
  const { t } = useTranslation('expenses');

  const theme = useTheme();
  const hideText = useMediaQuery(theme.breakpoints.down('sm'));

  const chipText = (() => {
    switch (reason) {
      case 'approved':
        return t('edit.LockedChipApproved');
      case 'invoiced':
        return t('edit.LockedChipInvoiced');
      case 'project_inactive':
      case 'contract_inactive':
        return t('edit.LockedChipInactive');
      default:
        return '';
    }
  })();

  const chipColor = (() => {
    switch (reason) {
      case 'approved':
        return 'success';
      case 'invoiced':
      case 'project_inactive':
      case 'contract_inactive':
        return 'warning';
      default:
        return 'default';
    }
  })();

  const chipIconName = (() => {
    switch (reason) {
      case 'approved':
        return 'lock';
      case 'invoiced':
        return areaIcons.invoices;
      case 'project_inactive':
      case 'contract_inactive':
        return 'block';
      default:
        return 'lock';
    }
  })();

  const helperText = (() => {
    switch (reason) {
      case 'approved':
        return t('edit.LockedExpenseApprovedMessage');
      case 'invoiced':
        return t('edit.LockedExpenseInvoicedMessage');
      case 'project_inactive':
        return t('edit.LockedExpenseProjectInactiveMessage');
      case 'contract_inactive':
        return t('edit.LockedExpenseContractInactiveMessage');
      default:
        return '';
    }
  })();

  return (
    <Stack direction="row" alignItems="center" gap={2}>
      {!hideText && (
        <Typography
          component="p"
          variant="bodyRegular"
          color="textPrimary"
          align="right"
        >
          {helperText}
        </Typography>
      )}

      <Chip
        className={styles.Chip}
        icon={<Icon name={chipIconName} size={16} />}
        color={chipColor}
        label={chipText}
        size="small"
      />
    </Stack>
  );
};
